import React, { useEffect } from "react";
import { TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import { getTileCached, saveTileCached } from "../../utils/tileCacheManager";

type MapLayerKey = "street" | "topo" | "satellite" | "hillshade" | "opentopomap" | "hybrid";

interface BaseTileLayerProps {
  mapLayer: MapLayerKey;
}

const baseLayers: Record<Exclude<MapLayerKey, "hillshade" | "hybrid">, { url: string; attribution: string; maxZoom: number }> = {
  street: {
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    maxZoom: 19,
  },
  topo: {
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}",
    attribution: "Tiles &copy; Esri",
    maxZoom: 19,
  },
  satellite: {
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution: "&copy; Esri, Maxar, GeoEye contributors",
    maxZoom: 19,
  },
  opentopomap: {
    url: "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png",
    attribution: '&copy; <a href="https://opentopomap.org">OpenTopoMap</a> contributors, &copy; OpenStreetMap',
    maxZoom: 17,
  },
};

const hillshadeLayer = {
  url: "https://tiles.wmflabs.org/hillshading/{z}/{x}/{y}.png",
  attribution: "&copy; Hillshade",
};

// ─── CachedTileLayer ────────────────────────────────────────────────────────
// Leaflet custom tile layer che:
//  1. Legge prima da IndexedDB (cache offline)
//  2. Se non presente, scarica dalla rete E salva automaticamente in cache
//     (caching passivo — le tile già viste sono disponibili offline)
// La chiave in cache è "{layerPrefix}/{z}/{x}/{y}" per evitare collisioni.

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CachedTileLayerClass = (L.TileLayer as any).extend({
  createTile(coords: L.Coords, done: L.DoneCallback): HTMLElement {
    const tile = document.createElement("img");
    tile.alt = "";
    tile.setAttribute("role", "presentation");

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const layerPrefix: string = (this as any).options.layerPrefix ?? 'satellite';
    const key = `${layerPrefix}/${coords.z}/${coords.x}/${coords.y}`;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const networkUrl: string = (this as any).getTileUrl(coords);

    const useBlob = (blob: Blob) => {
      const objUrl = URL.createObjectURL(blob);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      tile.onload  = () => { URL.revokeObjectURL(objUrl); (done as any)(null, tile); };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      tile.onerror = (e: any) => (done as any)(e, tile);
      tile.src = objUrl;
    };

    const fallbackNetwork = () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      tile.onload  = () => (done as any)(null, tile);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      tile.onerror = (e: any) => (done as any)(e, tile);
      tile.src = networkUrl;
    };

    getTileCached(key)
      .then((cached) => {
        if (cached) {
          useBlob(cached);
        } else {
          // Scarica dalla rete e salva in cache (caching passivo)
          fetch(networkUrl)
            .then((r) => r.ok ? r.blob() : null)
            .then((blob) => {
              if (blob) {
                saveTileCached(key, blob).catch(() => {});
                useBlob(blob);
              } else {
                fallbackNetwork();
              }
            })
            .catch(fallbackNetwork);
        }
      })
      .catch(fallbackNetwork);

    return tile;
  },
});

interface CachedTileLayerProps {
  url: string;
  attribution: string;
  maxZoom: number;
  layerPrefix: string;
}

const CachedTileLayer: React.FC<CachedTileLayerProps> = ({ url, attribution, maxZoom, layerPrefix }) => {
  const map = useMap();

  useEffect(() => {
    const layer = new CachedTileLayerClass(url, { attribution, maxZoom, layerPrefix });
    layer.addTo(map);
    return () => { map.removeLayer(layer); };
  }, [map, url, attribution, maxZoom, layerPrefix]);

  return null;
};

// ─── BaseTileLayer ────────────────────────────────────────────────────────────

export const BaseTileLayer: React.FC<BaseTileLayerProps> = ({ mapLayer }) => {
  const showHillshadeOverlay = mapLayer === "hillshade";
  const showHybridOverlay = mapLayer === "hybrid";
  const baseKey = showHillshadeOverlay ? "topo" : showHybridOverlay ? "satellite" : mapLayer;
  const baseConfig = baseLayers[baseKey as Exclude<MapLayerKey, "hillshade" | "hybrid">];

  return (
    <>
      <CachedTileLayer
        key={`cached-${baseKey}`}
        url={baseConfig.url}
        attribution={baseConfig.attribution}
        maxZoom={baseConfig.maxZoom}
        layerPrefix={baseKey}
      />

      {showHillshadeOverlay && (
        <TileLayer
          key="overlay-hillshade"
          attribution={hillshadeLayer.attribution}
          url={hillshadeLayer.url}
          opacity={0.45}
        />
      )}

      {showHybridOverlay && (
        <TileLayer
          key="overlay-hybrid-osm"
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          opacity={0.6}
          maxZoom={19}
        />
      )}
    </>
  );
};

export default BaseTileLayer;
